"use client";

import { ArrowBigDown } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { memo, useCallback } from "react";
import { Button } from "./ui/button";

import { REQUEST_PARAMS, SORT_DIR } from "@/constants";
import { useSetQuery } from "@/hooks";
import { cn } from "@/lib";
import type { SortByType, SortDirType } from "@/types";

const stylesArrow: Record<SortDirType, string> = {
  base: "opacity-50",
  asc: "rotate-180",
  desc: "",
};

type SortButtonProps = { sortBy: SortByType };

export const SortButton = memo(({ sortBy }: SortButtonProps) => {
  const sp = useSearchParams();
  const { setQuery, isPending } = useSetQuery();
  const currentSortBy = sp.get(REQUEST_PARAMS.sort_by);
  const currentSortDir = (sp.get(REQUEST_PARAMS.sort_dir) as SortDirType | null) ?? SORT_DIR.base;
  const dir: SortDirType = currentSortBy === sortBy ? currentSortDir : SORT_DIR.base;

  const handleClick = useCallback(() => {
    if (dir === SORT_DIR.base) {
      setQuery({ sort_by: sortBy, sort_dir: SORT_DIR.asc });
    } else if (dir === SORT_DIR.asc) {
      setQuery({ sort_by: sortBy, sort_dir: SORT_DIR.desc });
    } else {
      setQuery({ sort_by: undefined, sort_dir: undefined });
    }
  }, [dir, sortBy, setQuery]);

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="ghost"
      aria-label="Sort"
      aria-pressed={dir !== SORT_DIR.base}
      disabled={isPending}
      title={dir === SORT_DIR.base ? "Sort asc" : dir === SORT_DIR.asc ? "Sort desc" : "Clear sort"}
    >
      <ArrowBigDown
        fill="currentColor"
        className={cn("text-primary size-3", { "animate-spin": isPending }, stylesArrow[dir])}
      />
    </Button>
  );
});

SortButton.displayName = "SortButton";
